/**
 * Centralized API client for all HTTP requests
 * Eliminates duplicate fetch boilerplate and provides consistent error handling
 * Automatically includes CSRF token for state-changing requests (POST, PUT, DELETE)
 */

/**
 * CSRF token for protecting state-changing requests
 * Extracted from X-CSRF-Token response header on first request
 */
let csrfToken = '';

/**
 * Extract CSRF token from response headers and store it
 */
function extractCSRFToken(response: Response): void {
    const token = response.headers.get('X-CSRF-Token');
    if (token) {
        csrfToken = token;
    }
}

export class ApiError extends Error {
    status: number;
    statusText: string;
    url: string;

    constructor(status: number, statusText: string, url: string) {
        super(`API request failed: ${status} ${statusText}`);
        this.name = 'ApiError';
        this.status = status;
        this.statusText = statusText;
        this.url = url;
    }
}

/**
 * Make a GET request
 */
export async function apiGet<T>(url: string): Promise<T> {
    try {
        const response = await fetch(url, {
            credentials: 'include'
        });

        if (!response.ok) {
            throw new ApiError(response.status, response.statusText, response.url);
        }

        // Extract CSRF token from response for future requests
        extractCSRFToken(response);

        return await response.json();
    } catch (error) {
        if (error instanceof ApiError) {
            throw error;
        }
        throw new ApiError(0, (error as Error).message, url);
    }
}

/**
 * Make a POST request
 */
export async function apiPost<T>(url: string, body: any): Promise<T> {
    try {
        const headers: Record<string, string> = {
            'Content-Type': 'application/json'
        };

        // Include CSRF token if available
        if (csrfToken) {
            headers['X-CSRF-Token'] = csrfToken;
        }

        const response = await fetch(url, {
            method: 'POST',
            headers,
            body: JSON.stringify(body),
            credentials: 'include'
        });

        if (!response.ok) {
            throw new ApiError(response.status, response.statusText, response.url);
        }

        // Extract CSRF token from response for next request
        extractCSRFToken(response);

        return await response.json();
    } catch (error) {
        if (error instanceof ApiError) {
            throw error;
        }
        throw new ApiError(0, (error as Error).message, url);
    }
}

/**
 * Make a PUT request
 */
export async function apiPut<T>(url: string, body: any): Promise<T> {
    try {
        const headers: Record<string, string> = {
            'Content-Type': 'application/json'
        };

        // Include CSRF token if available
        if (csrfToken) {
            headers['X-CSRF-Token'] = csrfToken;
        }

        const response = await fetch(url, {
            method: 'PUT',
            headers,
            body: JSON.stringify(body),
            credentials: 'include'
        });

        if (!response.ok) {
            throw new ApiError(response.status, response.statusText, response.url);
        }

        // Extract CSRF token from response for next request
        extractCSRFToken(response);

        return await response.json();
    } catch (error) {
        if (error instanceof ApiError) {
            throw error;
        }
        throw new ApiError(0, (error as Error).message, url);
    }
}

/**
 * Make a DELETE request
 */
export async function apiDelete<T>(url: string): Promise<T | undefined> {
    try {
        const fetchOptions: any = {
            method: 'DELETE',
            credentials: 'include'
        };

        // Include CSRF token if available
        if (csrfToken) {
            fetchOptions.headers = { 'X-CSRF-Token': csrfToken };
        }

        const response = await fetch(url, fetchOptions);

        if (!response.ok) {
            throw new ApiError(response.status, response.statusText, response.url);
        }

        // Extract CSRF token from response for next request
        extractCSRFToken(response);

        // Handle 204 No Content
        if (response.status === 204) {
            return undefined;
        }

        return await response.json();
    } catch (error) {
        if (error instanceof ApiError) {
            throw error;
        }
        throw new ApiError(0, (error as Error).message, url);
    }
}
